/**
 * The admin section's map: every page, in four groups, in one place.
 *
 * WHY ONE LIST
 *   The nav (PortalShell), the directory on the admin home, and the "where am
 *   I" highlight all used to carry their own copy of the page list, and they
 *   drifted: a page was in the nav but not the directory, or the other way
 *   round. This is the single source; the three surfaces read it.
 *
 * WHY GROUPS (owner ask, 2026-10-15)
 *   Twenty-three tabs in one row did not fit a laptop, and the pages opened
 *   every morning sat beside ones opened once a quarter. The groups are read
 *   left to right by how often they are opened:
 *
 *     Daily       the dashboards: who joined, who pays, who stays, who left.
 *     Analytics   the weekly reads: growth, acquisition, devices, pages, loop.
 *     Tools       things that CHANGE the app or send mail: events, dexes, email.
 *     Reference   plumbing and the archive: backups, wiring, data trail, and
 *                 the tabs for campaigns that have ended.
 *
 *   A page that has had its day (the Android closed beta filled 2026-09-14,
 *   the Play launch list was mailed 2026-10-08) keeps its route and its data
 *   but leaves the nav: it lives on the Archive page under Reference, so a
 *   bookmark still works and nothing is deleted, while the daily row stays
 *   short enough to read.
 */
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  AlertTriangle,
  Archive,
  BarChart3,
  CalendarClock,
  Database,
  Flag,
  GitBranch,
  Globe,
  Heart,
  HardDrive,
  Layers,
  LayoutDashboard,
  Mail,
  MousePointerClick,
  Plug,
  Rocket,
  Send,
  Share2,
  ShieldCheck,
  Smartphone,
  TrendingUp,
  User,
  UserMinus,
  UserPlus,
  Users,
} from "lucide-react";

export type AdminGroupKey = "daily" | "analytics" | "tools" | "reference";

export interface AdminNavItem {
  /** PortalShell `active` id. */
  id: string;
  href: string;
  /** The tab label: short, one line. */
  label: string;
  /** The directory card's one-sentence description. */
  blurb: string;
  icon: LucideIcon;
  /**
   * Other `active` ids that belong to this tab: a sub-page (Dex backups under
   * Dex) or a hidden route (Data trail). They highlight this tab without
   * getting one of their own.
   */
  aliases?: string[];
}

export interface AdminNavGroup {
  key: AdminGroupKey;
  label: string;
  /** One line under the group name on the home directory. */
  blurb: string;
  items: AdminNavItem[];
}

export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
  {
    key: "daily",
    label: "Daily",
    blurb: "The morning read: what happened, who pays, who is staying, who is leaving.",
    items: [
      {
        id: "admin-home",
        href: "/portal/admin",
        label: "Overview",
        blurb: "The last 24 hours, the retention headline, and anything that needs attention today.",
        icon: LayoutDashboard,
      },
      {
        id: "admin-analytics",
        href: "/portal/admin/analytics",
        label: "Pro",
        blurb: "Subscription funnel, MRR, paywall attribution, and the daily actives, scans and errors.",
        icon: BarChart3,
      },
      {
        id: "admin-captures",
        href: "/portal/admin/captures",
        label: "Accounts",
        blurb: "Every account as a row: dex, scanning, device, country, Pro kind, backup state. Filter and sort.",
        icon: Users,
      },
      {
        id: "admin-new-joiners",
        href: "/portal/admin/new-joiners",
        label: "Joiners",
        blurb: "The newest sign-ups and how their first session went: where from, what they did, did they come back.",
        icon: UserPlus,
      },
      {
        id: "admin-user",
        href: "/portal/admin/user",
        label: "User",
        blurb: "One trainer in full: identity, device, where they came from, their whole journey, dexes, Pro.",
        icon: User,
      },
      {
        id: "admin-loyal",
        href: "/portal/admin/loyal",
        label: "Loyal",
        blurb: "Regulars and Pro against everyone else: what they live in, how they scan, and who is slipping.",
        icon: Heart,
      },
      {
        id: "admin-deletions",
        href: "/portal/admin/deletions",
        label: "Leaving",
        blurb: "Deletion requests with what they said, their phone, country, where they came from and what they did first.",
        icon: UserMinus,
      },
    ],
  },
  {
    key: "analytics",
    label: "Analytics",
    blurb: "The weekly reads: trends, sources, phones, pages and the invite loop.",
    items: [
      {
        id: "admin-growth",
        href: "/portal/admin/growth",
        label: "Growth",
        blurb: "Stickiness, lifecycle, activation funnel and the retention curve.",
        icon: TrendingUp,
      },
      {
        id: "admin-acquisition",
        href: "/portal/admin/acquisition",
        label: "Where from",
        blurb: "Where sign-ups came from by day, with the post and ad log beside them.",
        icon: Globe,
      },
      {
        id: "admin-devices",
        href: "/portal/admin/devices",
        label: "Devices",
        blurb: "Scan success by phone and OS, and which phones lose people.",
        icon: Smartphone,
      },
      {
        id: "admin-browsing",
        href: "/portal/admin/browsing",
        label: "Browsing",
        blurb: "Which pages get used and which are ignored.",
        icon: MousePointerClick,
      },
      {
        id: "admin-referrals",
        href: "/portal/admin/referrals",
        label: "Friends",
        blurb: "The invite loop rung by rung, and which rung the number dies on.",
        icon: Share2,
      },
      {
        id: "admin-activity",
        href: "/portal/admin/activity",
        label: "Activity",
        blurb: "Raw event stream, feedback inbox, error log with triage, signup cohorts.",
        icon: Activity,
      },
    ],
  },
  {
    key: "tools",
    label: "Tools",
    blurb: "Things that change the app or send mail.",
    items: [
      {
        id: "admin-events",
        href: "/portal/admin/events",
        label: "Events",
        blurb: "Review each event's species and bonuses and link the right ones by hand.",
        icon: CalendarClock,
      },
      {
        id: "admin-dex",
        href: "/portal/admin/dex",
        label: "Dex",
        blurb: "Per-account dex backups and restore, the Mega / G-Max / Dynamax config, trade rules.",
        icon: Layers,
        aliases: ["admin-dex-backups", "admin-dex-config", "admin-dex-trading"],
      },
      {
        id: "admin-email-compose",
        href: "/portal/admin/email-compose",
        label: "Compose",
        blurb: "Write one email to one trainer in the Nexus Dex shell, preview it, send it.",
        icon: Send,
      },
      {
        id: "admin-email-inbox",
        href: "/portal/admin/email-inbox",
        label: "Email inbox",
        blurb: "Outbound transactional email, as the Worker actually sent it.",
        icon: Mail,
      },
      {
        id: "admin-raid-reports",
        href: "/portal/admin/raid-reports",
        label: "Raid reports",
        blurb: "Hosts trainers reported after a raid, with the ratings that were not reports.",
        icon: Flag,
      },
    ],
  },
  {
    key: "reference",
    label: "Reference",
    blurb: "Plumbing, and the tabs whose job is done.",
    items: [
      {
        id: "admin-system",
        href: "/portal/admin/system",
        label: "System",
        blurb: "Database backups: the nightly D1 export, run history, back up now.",
        icon: HardDrive,
      },
      {
        id: "admin-wiring",
        href: "/portal/admin/wiring",
        label: "Wiring",
        blurb: "Provider capability matrix, journey health and revenue signals.",
        icon: Plug,
      },
      {
        id: "admin-dataflow",
        href: "/portal/admin/dataflow",
        label: "Data trail",
        blurb: "Architecture map: how user input moves through consent, API and storage.",
        icon: GitBranch,
      },
      {
        id: "admin-my-data",
        href: "/portal/admin/my-data",
        label: "My data",
        blurb: "Everything the system holds about your own account.",
        icon: ShieldCheck,
      },
      {
        id: "admin-archive",
        href: "/portal/admin/archive",
        label: "Archive",
        blurb: "Tabs for campaigns that have ended. Routes and data kept; nothing deleted.",
        icon: Archive,
        aliases: ["admin-beta-signups", "admin-play-notify", "admin-notice-winback"],
      },
    ],
  },
];

/**
 * The archived tabs: pages whose campaign is over. Each keeps its route and
 * its data; the Archive page lists them and each page carries a banner.
 */
export interface ArchivedAdminPage {
  id: string;
  href: string;
  label: string;
  /** Why it is in the archive, with the date, in one sentence. */
  reason: string;
  icon: LucideIcon;
}

export const ADMIN_ARCHIVED: ArchivedAdminPage[] = [
  {
    id: "admin-beta-signups",
    href: "/portal/admin/beta-signups",
    label: "Android beta",
    reason:
      "The closed beta filled on 14 September 2026 and the thank-you mail went out; the tester list is no longer added to.",
    icon: Smartphone,
  },
  {
    id: "admin-play-notify",
    href: "/portal/admin/play-notify",
    label: "Play launch",
    reason:
      "Nexus Dex went public on Google Play on 8 October 2026 and the launch mail was sent; the notify list has done its job.",
    icon: Rocket,
  },
  {
    id: "admin-notice-winback",
    href: "/portal/admin/notice-winback",
    label: "Win-back",
    reason:
      "The profile data notice was a one-off mailing around the 2 October 2026 purge; its reach and returns are kept here for the record.",
    icon: AlertTriangle,
  },
];

/** Every item, flattened, in nav order. */
export function allAdminNavItems(): AdminNavItem[] {
  return ADMIN_NAV_GROUPS.flatMap((g) => g.items);
}

/**
 * The group an `active` id belongs to, by its own id or an alias. Unknown ids
 * (a page added later and not yet mapped) land in Daily, so the nav always
 * shows a tab row rather than an empty bar.
 */
export function adminGroupFor(activeId: string | undefined): AdminNavGroup {
  if (activeId) {
    for (const g of ADMIN_NAV_GROUPS) {
      if (g.items.some((it) => it.id === activeId || it.aliases?.includes(activeId))) return g;
    }
  }
  return ADMIN_NAV_GROUPS[0];
}

/** True when `active` is this tab or one of its sub-pages. */
export function adminTabIsActive(item: AdminNavItem, activeId: string | undefined): boolean {
  if (!activeId) return false;
  return item.id === activeId || (item.aliases?.includes(activeId) ?? false);
}

/** Icons the directory can look up by id (the groups own the canonical ones). */
export const ADMIN_ICON_BY_ID: Record<string, LucideIcon> = Object.fromEntries(
  allAdminNavItems().map((it) => [it.id, it.icon]),
);

// Referenced so the import list stays honest if an icon above is removed.
void Database;
